import Events from "../../helpers/events.js";
import Config from "./config.js";
import Fields from "./fields.js";
import Ids from "./ids.js";
import Loader from "./loader.js";
import Tracking from "./tracking.js";

/**
 * Api — the HubSpot methods, composed by index.js into ThryvApi.hubspot. Usage in index.js.
 */

/** hbspt.forms.create keys this module owns. Passing them in `options.options` is ignored. */
const RESERVED = [
  "region",
  "portalId",
  "formId",
  "target",
  "onFormReady",
  "onBeforeFormSubmit",
  "onFormSubmitted",
];

let counter = 0;

const warn = (...args) => console.warn("[ThryvApi.hubspot]", ...args);

/** HubSpot hands back a bare element in v2, a jQuery-like object in older embeds. */
const toElement = (form) => (form?.jquery ? form[0] : form);

/** A caller's callback must not break the built-ins, the event or the next callback. */
const safely = (fn, ...args) => {
  if (typeof fn !== "function") return;

  try {
    fn(...args);
  } catch (error) {
    console.error("[ThryvApi.hubspot] a callback threw:", error);
  }
};

/** The caller's extra hbspt options, minus the reserved keys (each one warned about). */
const extraOptions = (options = {}) =>
  Object.fromEntries(
    Object.entries(options).filter(([key]) => {
      if (!RESERVED.includes(key)) return true;

      warn(`options.${key} is reserved and was ignored`);

      return false;
    }),
  );

const Api = {
  Config,

  /** Whether hbspt is on the page. */
  ready: () => Loader.ready(),

  /** This visit's attribution from the URL: utm_* and impact_id (from irclickid). */
  utms: () => Fields.utms(),

  /**
   * Write values into a rendered form — empty, defined fields only, unless forced.
   *
   * @param  {HTMLFormElement} form
   * @param  {object} fields  { fieldName: value }
   * @param  {{force?: string[]|true}} opts
   * @return {string[]} the names written
   */
  fill: (form, fields, opts = {}) => Fields.fill(toElement(form), fields, opts),

  /**
   * Change one field of a HubSpot form so HubSpot submits it (its form is React — see Fields.set). Use this, never
   * `input.value = …`.
   *
   * @param  {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} input
   * @param  {string} value
   * @return {boolean} whether anything changed
   */
  set: (input, value) => Fields.set(input, value),

  /** Tick / untick a checkbox, pick / clear a radio, so HubSpot sees it (Fields.check). */
  check: (input, checked) => Fields.check(input, checked),

  /**
   * Render a HubSpot form. Options are documented in index.js.
   *
   * @return {Promise<HTMLFormElement|null>}  null when HubSpot failed; never rejects
   */
  create(options = {}) {
    const target =
      typeof options.target === "string" ? document.querySelector(options.target) : options.target;
    const formId = options.formId || Config.formId(options.form);
    const ctx = { formId, name: options.form || Config.formName(formId), target };
    const detail = (extra = {}) => ({ formId: ctx.formId, name: ctx.name, ...extra });

    if (!target) {
      warn("create() found no target:", options.target);

      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      let settled = false;
      let timer = null;

      const settle = (form) => {
        if (settled) return;

        settled = true;
        clearTimeout(timer);
        resolve(form);
      };

      const fail = (error) => {
        if (settled) return;

        warn("form did not render:", error?.message || error);
        Events.emit("hubspot:failed", detail({ form: null, error }), target);
        settle(null);
      };

      // A form can name its own portal (the sandbox one, for the demo scheduler off the live site).
      const portalId = String(options.portalId || Config.portalId());

      if (!formId || !portalId) {
        fail(new Error(formId ? "no portal id" : `unknown form "${options.form ?? ""}"`));

        return;
      }

      // hbspt resolves `target` itself and needs a SELECTOR: handed a node it succeeds silently and
      // renders nothing. So the element gets an id if it has none.
      target.id ||= `hubspot-form-${++counter}`;

      timer = setTimeout(() => fail(new Error("timed out")), Config.timeout());

      Loader.load()
        .then((hbspt) => {
          hbspt.forms.create({
            ...extraOptions(options.options),
            region: Config.region(),
            portalId,
            formId,
            target: `#${target.id}`,

            // Runs even after a timeout — a late form still needs its owner and attribution.
            // HubSpot can call it again on re-render; everything here is idempotent.
            onFormReady: ($form) => {
              const form = toElement($form);
              const { values, force } = Fields.resolve(options);

              // The same form twice on a page shares HubSpot's ids — this copy's get its own (ids.js), before
              // anything else sees the form.
              Ids.watch(form, target.id);

              Fields.fill(form, values, { force });
              safely(options.onReady, form, ctx);
              Events.emit("hubspot:ready", detail({ form }), target);
              settle(form);
            },

            onBeforeFormSubmit: ($form, data) => {
              safely(options.onBeforeSubmit, toElement($form), data, ctx);
            },

            onFormSubmitted: ($form, data) => {
              const form = toElement($form);
              const redirect = options.redirect;

              Tracking.push(
                Tracking.payload(ctx, data),
                redirect ? () => window.location.assign(redirect) : null,
              );
              safely(options.onSubmitted, form, data, ctx);
              Events.emit("hubspot:submitted", detail({ form, data }), target);
            },
          });
        })
        .catch(fail);
    });
  },
};

export default Api;
